import Link from "next/link";
import { ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface LiveShowCardProps {
  title: string;
  subtitle?: string;
  description: string;
  tags: string[];
  liveSince?: string;
  statusLabel?: string;
  ctaHref: string;
  ctaLabel?: string;
  className?: string;
}

export function LiveShowCard({
  title,
  subtitle,
  description,
  tags,
  liveSince,
  statusLabel,
  ctaHref,
  ctaLabel = "Jetzt reinschauen",
  className,
}: LiveShowCardProps) {
  const liveStatusLabel =
    statusLabel ?? (liveSince ? `live seit ${liveSince}` : "jetzt live");
  const maxDescriptionLength = 120;
  const shortDescription =
    description.length > maxDescriptionLength
      ? `${description.slice(0, maxDescriptionLength).trimEnd()}...`
      : description;

  return (
    <Card
      className={[
        "flex min-h-[23rem] flex-col border border-border/60 bg-background/50 shadow-none",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <CardHeader className="min-h-[6.5rem] pb-3">
        <div className="space-y-3">
          <div className="flex w-full items-center justify-between gap-3 rounded-full border border-border/70 bg-card/70 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-studio-red">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="on-air-dot" />
              <span>On Air</span>
              <span className="h-3 w-px bg-border/80" />
              <div className="waveform-bars">
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
            <span className="shrink-0 text-[10px] font-medium tracking-[0.18em] text-muted-foreground">
              {liveStatusLabel}
            </span>
          </div>
          <div className="min-h-[3.25rem] text-center">
            <CardTitle className="text-xl text-foreground">{title}</CardTitle>
            {subtitle && (
              <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col space-y-3">
        <p className="min-h-[4.75rem] text-center text-sm leading-6 text-muted-foreground">
          {shortDescription}
        </p>
        <div className="min-h-[2.5rem] content-start flex flex-wrap justify-center gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border/60 bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-auto flex min-h-9 justify-end">
          <Button
            size="sm"
            className="h-9 rounded-full px-4 shadow-lg shadow-primary/20"
            render={<Link href={ctaHref} />}
          >
            <Play className="size-4 fill-current" />
            {ctaLabel}
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
